// Catalog.ID Client — Dashboard-module Context Detection
// ============================================================================
//
// Key Grant UI, the Connected Modules pane, and the local backend WebSocket
// are meaningful ONLY when the catalog.id client is loaded as a dashboard
// module (inside the dashboard iframe). The standalone catalog.id site has
// no local backend and no cross-module peers to grant keys to.
//
// The dashboard appends `?dp=<dashboardPort>` to each module iframe URL. If
// that query parameter is present on initial load, we are running inside the
// dashboard. We capture it once at module load so subsequent History API
// mutations don't flip the answer mid-session.
//
// Persistence across external redirects. Some flows leave the helper origin
// entirely (Stripe Checkout, OIDC RPs, …) and come back to a success URL that
// no longer carries `?dp=…` — at which point a naive query-string check would
// mistakenly conclude we're standalone and start aiming API calls at the
// helper port instead of the real server. To prevent that, the first load
// that DOES see `?dp=` writes the value into sessionStorage, and subsequent
// loads in the same browsing context (i.e. the same iframe) read it back.
// sessionStorage is per-tab/per-iframe so a fresh standalone tab never sees
// a value and behaves exactly as before.
//
// The Catalog ID nodes (apps/docs/plans/operator-codes.md §7.5). The bundle
// this helper serves runs at the helper's loopback origin (e.g.
// `localhost:61534`), but its API calls target Catalog ID nodes. No node is
// compiled in: on boot in dashboard mode we fetch `/api/internal/runtime-config`
// from the helper, which computes it from the desktop's VERIFIED trust-root
// directory — the ENTRY node (exposed via getApiBase()) and every code that
// runs cid with its address (getCidNodes(), used by ./origins to map an
// identity's origin CODE to an address; a URL a node answers with is never
// followed). The helper answers 503 until the desktop has told it a directory;
// the page waits that out. In standalone mode (the web client at a node's own
// origin) getApiBase() returns the empty string, so callers using
// `${apiBase}${path}` get same-origin relative URLs, and there is no directory.
// ============================================================================

import { describeUserAgent } from '../crypto/device-label';

interface DashboardCaptured {
    isDashboard: boolean;
    dashboardPort?: number;
}

const DASHBOARD_PORT_STORAGE_KEY = 'cid_dashboard_dp_v1';

function detectDashboardContext(): DashboardCaptured {
    try {
        const params = new URLSearchParams(window.location.search);
        const dpFromUrl = params.get('dp');
        if (dpFromUrl && /^\d+$/.test(dpFromUrl)) {
            // First load inside the iframe — persist so post-redirect loads
            // (Stripe success URL, OIDC return, etc.) still detect us as
            // a dashboard module even when the query string is gone.
            try { sessionStorage.setItem(DASHBOARD_PORT_STORAGE_KEY, dpFromUrl); } catch { /* storage unavailable */ }
            return { isDashboard: true, dashboardPort: Number(dpFromUrl) };
        }
        const dpFromStorage = sessionStorage.getItem(DASHBOARD_PORT_STORAGE_KEY);
        if (dpFromStorage && /^\d+$/.test(dpFromStorage)) {
            return { isDashboard: true, dashboardPort: Number(dpFromStorage) };
        }
    } catch {
        // document/URL/storage not available (SSR? test env?) — treat as non-dashboard
    }
    return { isDashboard: false };
}

const captured = detectDashboardContext();

/**
 * True when this catalog.id client is running as a dashboard module
 * (loaded inside the dashboard iframe with a `?dp=` parameter).
 */
export function isDashboardContext(): boolean {
    return captured.isDashboard;
}

/**
 * The dashboard's HTTP/WS port as captured from `?dp=` at load time, or
 * undefined when running as the standalone site.
 */
export function getDashboardPort(): number | undefined {
    return captured.dashboardPort;
}

// ────────────────────────────────────────────────────────────────────────
// The entry node and the directory — populated on first dashboard-mode boot.
// ────────────────────────────────────────────────────────────────────────

/** The helper's runtime-config, as this page uses it. */
export interface RuntimeConfig {
    /** The ENTRY node: where this page asks where an identity lives. No trailing slash. */
    entryNodeUrl: string;
    /** Every code the desktop's directory runs cid at → its base URL (no trailing slash). */
    cidNodes: Record<string, string>;
    devMode: boolean;
    /**
     * A human name for the machine the desktop helper runs on, e.g.
     * "evk-laptop (Linux)" — the prefill for the device-name field. Always
     * absent in standalone mode where the browser only knows its own user agent.
     */
    deviceLabelDefault?: string;
}

const RUNTIME_CONFIG_PATH = '/api/internal/runtime-config';

/** The longest wait between two asks while the helper has no directory. */
const RUNTIME_CONFIG_MAX_DELAY_MS = 5_000;
const RUNTIME_CONFIG_FIRST_DELAY_MS = 500;

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

let cachedApiBase: string = '';
let cachedDeviceLabelDefault: string | undefined;
/**
 * The directory's cid nodes. Null standalone — there is no directory, and a
 * node's own answers are the addresses. In dashboard mode never null: empty
 * until the runtime-config arrives, so nothing is reachable without it.
 */
let cachedCidNodes: Record<string, string> | null = captured.isDashboard ? {} : null;
let initPromise: Promise<void> | null = null;
let refreshPromise: Promise<void> | null = null;

function trimBase(url: string): string {
    return url.replace(/\/+$/, '');
}

/**
 * Pure: read a runtime-config answer. Null when it names no entry node or no
 * directory — including the pre-directory `{ realServerUrl }` shape, which
 * named a node and is refused rather than trusted.
 */
export function parseRuntimeConfig(body: unknown): RuntimeConfig | null {
    if (typeof body !== 'object' || body === null) return null;
    const b = body as Record<string, unknown>;
    if (typeof b.entryNodeUrl !== 'string' || !b.entryNodeUrl) return null;
    if (typeof b.cidNodes !== 'object' || b.cidNodes === null || Array.isArray(b.cidNodes)) return null;
    const cidNodes: Record<string, string> = {};
    for (const [code, url] of Object.entries(b.cidNodes as Record<string, unknown>)) {
        if (typeof url === 'string' && url) cidNodes[code] = trimBase(url);
    }
    const cfg: RuntimeConfig = {
        entryNodeUrl: trimBase(b.entryNodeUrl),
        cidNodes,
        devMode: b.devMode === true,
    };
    if (typeof b.deviceLabelDefault === 'string' && b.deviceLabelDefault) cfg.deviceLabelDefault = b.deviceLabelDefault;
    return cfg;
}

export interface FetchRuntimeConfigOptions {
    /** Injectable for tests. */
    sleep?: (ms: number) => Promise<void>;
    /** Told each time the helper answers 503, with its reason (`directory-unavailable`, `no-cid-node`). */
    onWaiting?: (reason: string) => void;
}

/**
 * Ask the helper for its runtime-config. A 503 means the desktop has not told
 * it a directory with a cid node in it yet: ask again, with a growing delay
 * (capped), for as long as it takes — there is no node to fall back to. Any
 * other failure is final (logged; null).
 */
export async function fetchRuntimeConfig(
    fetchImpl: FetchLike = (i, init) => fetch(i, init),
    opts: FetchRuntimeConfigOptions = {},
): Promise<RuntimeConfig | null> {
    const sleep = opts.sleep ?? ((ms: number) => new Promise<void>(r => setTimeout(r, ms)));
    let delay = RUNTIME_CONFIG_FIRST_DELAY_MS;
    for (;;) {
        let resp: Response;
        try {
            resp = await fetchImpl(RUNTIME_CONFIG_PATH);
        } catch (err) {
            console.error('[dashboard-context] failed to fetch runtime-config:', err);
            return null;
        }
        if (resp.status === 503) {
            let reason = 'directory-unavailable';
            try {
                const body = await resp.json() as { error?: unknown };
                if (typeof body?.error === 'string' && body.error) reason = body.error;
            } catch { /* no body: the default reason */ }
            opts.onWaiting?.(reason);
            await sleep(delay);
            delay = Math.min(delay * 2, RUNTIME_CONFIG_MAX_DELAY_MS);
            continue;
        }
        if (!resp.ok) {
            console.error(`[dashboard-context] ${RUNTIME_CONFIG_PATH} returned ${resp.status}; no Catalog ID node is known.`);
            return null;
        }
        let cfg: RuntimeConfig | null = null;
        try { cfg = parseRuntimeConfig(await resp.json()); } catch { cfg = null; }
        if (!cfg) console.error('[dashboard-context] runtime-config names no entry node or directory; no Catalog ID node is known.');
        return cfg;
    }
}

/** Take a runtime-config as this page's entry node and directory. */
export function applyRuntimeConfig(cfg: RuntimeConfig): void {
    cachedApiBase = trimBase(cfg.entryNodeUrl);
    cachedCidNodes = { ...cfg.cidNodes };
    cachedDeviceLabelDefault = cfg.deviceLabelDefault || undefined;
}

/** Test seam: back to what this module had at load (standalone under vitest). */
export function _resetRuntimeConfigForTests(): void {
    cachedApiBase = '';
    cachedDeviceLabelDefault = undefined;
    cachedCidNodes = captured.isDashboard ? {} : null;
    initPromise = null;
    refreshPromise = null;
}

/**
 * Initialise the entry node and the directory. In standalone mode this is a
 * no-op (apiBase stays empty → relative paths; no directory). In dashboard mode
 * this fetches the helper's `/api/internal/runtime-config` — waiting, for as
 * long as it takes, while the helper has no directory (`onWaiting` lets the
 * page say so) — and caches it.
 *
 * Idempotent and safe to call multiple times — the first call wins, subsequent
 * calls await the same promise. Call once at app boot before any API request.
 */
export async function initApiBase(onWaiting?: (reason: string) => void): Promise<void> {
    if (!captured.isDashboard) return;
    if (initPromise) return initPromise;
    initPromise = (async () => {
        const cfg = await fetchRuntimeConfig(undefined, { onWaiting });
        if (cfg) applyRuntimeConfig(cfg);
    })();
    return initPromise;
}

/**
 * Base URL prefix for API calls not made for one identity.
 *
 * - Standalone mode: empty string. Callers do `fetch(\`${apiBase}/api/foo\`)`
 *   and get a same-origin relative request, identical to today's behaviour.
 * - Dashboard mode: the ENTRY node from the directory (e.g.
 *   `http://localhost:5100` in dev). Cross-origin call from the helper-iframe
 *   origin to the node; the nodes' CORS allows loopback origins.
 *
 * Returns empty string until `initApiBase()` resolves; do not perform API
 * calls before then in dashboard mode.
 */
export function getApiBase(): string {
    return cachedApiBase;
}

/**
 * The directory's cid nodes (code → base URL), or null standalone, where there
 * is no directory and a node's own answers are the addresses.
 */
export function getCidNodes(): Readonly<Record<string, string>> | null {
    return cachedCidNodes;
}

/** The directory's address for `code`; undefined when it does not run cid there (or standalone). */
export function cidNodeUrl(code: string): string | undefined {
    const nodes = cachedCidNodes;
    return nodes && Object.prototype.hasOwnProperty.call(nodes, code) ? nodes[code] : undefined;
}

/**
 * A code was named that the directory does not list: ask the helper again,
 * with `?miss=1` — it asks the desktop to re-poll the ledger (rate-limited)
 * and waits briefly for the answer — and take the nodes it answers with. The
 * entry node stays what the page booted with. Concurrent calls share one
 * request; a failure keeps what is known. No-op standalone.
 */
export async function refreshCidNodes(fetchImpl: FetchLike = (i, init) => fetch(i, init)): Promise<void> {
    if (cachedCidNodes === null) return;
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
        try {
            const resp = await fetchImpl(`${RUNTIME_CONFIG_PATH}?miss=1`);
            if (!resp.ok) return;
            const cfg = parseRuntimeConfig(await resp.json());
            if (cfg) cachedCidNodes = { ...cfg.cidNodes };
        } catch {
            /* the helper could not answer: what is known stands */
        } finally {
            refreshPromise = null;
        }
    })();
    return refreshPromise;
}

/**
 * The desktop helper's machine name, cached by {@link initApiBase}, or
 * undefined in standalone mode.
 * Feed it to {@link defaultDeviceLabel} rather than using it raw.
 */
export function getDeviceLabelDefault(): string | undefined {
    return cachedDeviceLabelDefault;
}

/**
 * Pure: the prefill for the "Device name" field. The helper's machine name
 * when we have one, otherwise a description of the browser
 * ("Chrome on Windows"). Never empty — an empty prefill invites an unnamed
 * device, and device names are what make the Devices pane legible.
 */
export function defaultDeviceLabel(helperDefault: string | undefined, ua: string): string {
    return helperDefault ? helperDefault : describeUserAgent(ua);
}

/** {@link defaultDeviceLabel} using the cached helper name and this browser's
 *  user agent. */
export function getDefaultDeviceLabel(): string {
    let ua = '';
    try { ua = navigator.userAgent; } catch { /* no navigator (tests) */ }
    return defaultDeviceLabel(cachedDeviceLabelDefault, ua);
}

// ────────────────────────────────────────────────────────────────────────
// Node host for printed material (Account Unlock Sheet "Open <host> and choose …").
// ────────────────────────────────────────────────────────────────────────

/**
 * Pure resolver: the host (plus any non-root path base) a person should open
 * to reach the CID node that owns the account. Prefers `apiBase` — in
 * dashboard mode `locationHost` is the desktop helper's loopback origin
 * (e.g. `localhost:61534`), which is not the node and does not outlive the
 * desktop process. Falls back to `locationHost` in standalone mode, where
 * the page origin IS the node; undefined when neither is known.
 */
export function resolveNodeHost(apiBase: string, locationHost: string): string | undefined {
    if (apiBase) {
        try {
            const u = new URL(apiBase);
            const path = u.pathname.replace(/\/+$/, '');
            return `${u.host}${path}`;
        } catch {
            // not an absolute URL — fall through to the page origin
        }
    }
    return locationHost || undefined;
}

// The host to print for ONE identity is `nodeHostFor(cid)` in `./origins`:
// printed sheets name the identity's origin (docs/cid-governance.md M7a),
// which need not be this entry node.

// ────────────────────────────────────────────────────────────────────────
// Links that open a NEW browsing context on this origin.
// ────────────────────────────────────────────────────────────────────────

/**
 * Pure: append `?dp=<port>` to an internal path when a dashboard port is
 * known. Dashboard detection reads `?dp=` from the URL or from per-tab
 * sessionStorage; a link opened in a new tab (target=_blank) inherits
 * neither, so without this the new tab boots standalone, `getApiBase()`
 * stays empty and every API call hits the helper — which serves static
 * files and answers POST with 405.
 */
export function withDashboardPort(path: string, dashboardPort: number | undefined): string {
    if (dashboardPort === undefined) return path;
    return `${path}${path.includes('?') ? '&' : '?'}dp=${dashboardPort}`;
}

/** withDashboardPort() using the port captured at load time. */
export function dashboardHref(path: string): string {
    return withDashboardPort(path, captured.dashboardPort);
}

